import $settings from 'src/include/settings.js';
import $log from 'src/include/log.js';
import $cfetch from 'src/include/cfetch.js';
import $UserSession from 'src/include/usersession.js';

/**
 * Class used for learning outcomes manipulation.
 * @class
 */

class LearningOutcomes {
    constructor(nodes, userSession = $UserSession) {
        this.$UserSession = userSession;
        this.$cfetch = $cfetch;
        this.$log = $log;
        this.$settings = $settings;
        this.nodes = nodes;
    }
     /**
        * List all of the learning outcomes
        * @param {Object} filters - filters the outcomes by
        * - group_id: choose a learning outcome group
        * - unmapped: display unmapped outcomes only
        * - root_id: display learning outcomes linked to a specific curriculum
        * - search: query for learning outcomes title
        * - include_deleted: show deleted outcomes
        * 
        * It is also possible to pass the standard pagination filters,
        * @see InvApiList.getPagination 
        * 
        * @returns {<Promise>Array} - the Learning Outcomes
        *
    */
    list(filters) {
         
        const data = filters || {};
        data.username = this.$UserSession.getValue("username");
        data.authToken = this.$UserSession.getValue("authToken");

        return this.$cfetch(`${this.$settings.APIBaseURL}/learningoutcome/list`, {
            method: "post",
            data: data
        })
        .then((res) => {
            if (res.status !== "SUCCESS") throw new Error('Could not fetch LOs');
            return res;
        }).catch(error => {
            this.$log.error.call(this, error);
        });
    }

    /**
        * Get learning outcome
        * @param {Number} id - the ID of the learning outcomes
        * 
        * @returns {<Promise>Object} - the learning outcome
        * {
            id: [Number],
            learningoutcomegroup_id: [Number],
            title: [String],
            note: [String],
            code: [String]
        };
        *
    */
    get(id) {
        const data = {
            id : id
        }
        data.username = this.$UserSession.getValue("username");
        data.authToken = this.$UserSession.getValue("authToken");

        return this.$cfetch(`${$settings.APIBaseURL}/learningoutcome/get`, {
            method: "post",
            data: data
        })
        .then((res) => {
            if (res.status !== "SUCCESS") throw new Error('Could not fetch learning outcome');
                let outcome = res.result;
                return outcome
        }).catch(error => {
            this.$log.error.call(this, error);
        });
    }

    /**
        * 
        * @param {Object} outcome - it has the following structure
        * {
            id: [Number],
            learningoutcomegroup_id: [Number],
            title: [String],
            note: [String],
            code: [String]
        }
        * 
        * @returns {Object<Promise>} - the saved learning outcome 
        
    */
    save(outcome){
        const data = outcome || {};
        data.username = this.$UserSession.getValue("username");
        data.authToken = this.$UserSession.getValue("authToken");
        
        return this.$cfetch(`${this.$settings.APIBaseURL}/learningoutcome/save`, {
            method: "post",
            data: data
        })
        .then((res) => {
            if (res.status !== "SUCCESS") throw new Error('Could not save learning outcome')
                
            return res.result;
        })
        .catch((error) => {this.$log.error.call(this, error)});
    }

    /**
     * 
     * @param {Number} id - the id of the learning outcome to be deleted
     * {
        id: [Number],
        learningoutcomegroup_id: [Number],
        title: [String],
        note: [String],
        code: [String]
    }
        * 
        * @returns {String<Promise>} - a message indicating whether it was deleted or not 
    
    */
    delete(id){
        const data = {
            id: id
        };

        data.username = this.$UserSession.getValue("username");
        data.authToken = this.$UserSession.getValue("authToken");
        
        return this.$cfetch(`${this.$settings.APIBaseURL}/learningoutcome/delete`, {
            method: "post",
            data: data
        })
        .then((res) => {
            if (res.status !== "SUCCESS") throw new Error('Could not save learning outcome')
                
            return res.status;
        })
        .catch((error) => {this.$log.error.call(this, error)});
    }

    /**
        * List learning outcomes groups
        * @param {Object} filters - filters the outcomes by
        * - search: query for learning outcomes title
        * - include_deleted: show deleted outcomes
        * 
        * It is also possible to pass the standard pagination filters,
        * @see InvApiList.getPagination 
        * @returns {<Promise>Array} - The Learning Outcomes Groups
        *
    */
    listGroups(filters) {
        const data = filters || {};
        data.username = this.$UserSession.getValue("username");
        data.authToken = this.$UserSession.getValue("authToken");
        
        return this.$cfetch(`${$settings.APIBaseURL}/learningoutcome/group/list`, {
            method: "post",
            data: data
        })
        .then((res) => {
            if (res.status !== "SUCCESS") throw new Error('Could not fetch LOGs');
            return res;
        }).catch(error => {
            this.$log.error.call(this, error);
        });
    }

    /**
        * Get learning outcomes group
        * @param {Number} id - the ID of the learning outcomes group
        * 
        * @returns {<Promise>Object} - the learning outcomes group
        * {
            id: [Number],
            title: [String],
            note: [String],
        };
    */
    getGroup(id) {
        const data = {
            id: id
        };

        data.username = this.$UserSession.getValue("username");
        data.authToken = this.$UserSession.getValue("authToken");
        
        return this.$cfetch(`${$settings.APIBaseURL}/learningoutcome/group/get`, {
            method: "post",
            data: data
        })
        .then((res) => {
            if (res.status !== "SUCCESS") throw new Error('Could not fetch LOG');
                let outcomesGroup = res.result;
                return outcomesGroup
        }).catch(error => {
            this.$log.error.call(this, error);
        });
    }

    /**
         * 
         * @param {Object} group - it has the following structure
         * {
            id: [Number],
            title: [String],
            note: [String],
        }
         * 
         * @returns {Object<Promise>} - the saved learning outcome
        
     */
    saveGroup(group){
        const data = group || {};
        data.username = this.$UserSession.getValue("username");
        data.authToken = this.$UserSession.getValue("authToken");
        

        return this.$cfetch(`${this.$settings.APIBaseURL}/learningoutcome/group/save`, {
            method: "post",
            data: data
        })
        .then((res) => {
            if (res.status !== "SUCCESS") throw new Error('Could not save learning outcome group')
                
            return res.result;
        })
        .catch((error) => {this.$log.error.call(this, error)});
    }

}

export default LearningOutcomes